import moment from 'moment';

const WEIGHT = {
    "Cyclomatic" : 3.33333,
    "Stage Type" : 1.42857,
    "Transformer LOC" : 0.06897
}
const normalise = (value, prop)  => value * WEIGHT[prop];
const OverallComplexity = "Complexity Rating"

const calculateTotalComplexity = (assets) => {
    let newAssets = JSON.parse(JSON.stringify(assets)); // make a copy

    for (let assetName in newAssets) {
        const asset = newAssets[assetName];

        if (!("complexityScore" in asset)) continue;

        let complexity = 0;
        for (let queryName in asset.complexityScore) {
            const queryResult = asset.complexityScore[queryName];

            complexity += !isNaN(queryResult) ? Math.round(normalise(Number(queryResult), queryName)) : 0;
        }
        asset.complexityScore.total = complexity;
    }

    return newAssets;
}

// If we have separate compliance "blockers" and "warnings", combine them
const combineCompliance = (assets) => {

    let newAssets = JSON.parse(JSON.stringify(assets)); // make a copy

    for (let assetName in newAssets) {
        const asset = newAssets[assetName];

        if (!("compliance" in asset)) asset.compliance = [];

        if ("complianceBlocker" in asset) {
            asset.compliance = asset.compliance.concat(asset.complianceBlocker);
            delete asset.complianceBlocker;
        }
        if ("complianceWarning" in asset) {
            asset.compliance = asset.compliance.concat(asset.complianceWarning);
            delete asset.complianceWarning;
        }
    }

    return newAssets;
}

const addNameToAssets = (assets) => {
    let newAssets = JSON.parse(JSON.stringify(assets)); // make a copy

    for (let assetName in newAssets) {
        const asset = newAssets[assetName];

        asset.asset = assetName;
        asset.archive = "unknown";
        asset.engine = "unknown";
        asset.project = "unknown";
    }

    return newAssets;
}

const flattenHeirarchy = (data) => {
    let newAssets = {};

    for (let archiveName in data) {

        const archive = data[archiveName];
        for (let engineName in archive) {

            const engine = archive[engineName];
            for (let projectName in engine) {

                const project = engine[projectName];
                for (let assetName in project) {

                    const asset = JSON.parse(JSON.stringify(project[assetName])); // make a copy
                    asset.archive = archiveName;
                    asset.engine = engineName;
                    asset.project = projectName;
                    asset.asset = assetName;

                    newAssets[`${archiveName}/${engineName}/${projectName}/${assetName}`] = asset;
                }
            }
        }
    }

    return newAssets;
}

const parseUrl = (url) => {
    let fileName,
        clientName,
        emailAddress,
        date;

    if (url === "sample.csv") {
        fileName   = "sample";
        clientName = "Sample";
    } else {
        const urlParts = url.split("/");

        const fileNameWithDate = urlParts[7];

        clientName = urlParts[6];
        emailAddress = urlParts[5];

        fileName = fileNameWithDate.substring(14);

        const datetime = `${fileNameWithDate.split("_")[0]}_${fileNameWithDate.split("_")[1]}`;
        if (datetime) {
            const momentObj = moment(datetime, 'YYYYMMDD_HHmm');
            date = momentObj.format('DD-MMM-YYYY HH:mm');
        }
    }

    return {
        fileName,
        clientName,
        emailAddress,
        date
    };
}

const getDateFromUrl = (url) => {
    const fileNameWithDate = url.split("/")[7];
    if (!fileNameWithDate) return;

    const datetime = `${fileNameWithDate.split("_")[0]}_${fileNameWithDate.split("_")[1]}`;

    let date;
    if (datetime) {
        const momentObj = moment(datetime, 'YYYYMMDD_HHmm');
        date = momentObj.format('DD-MMM-YYYY HH:mm');
    }

    return date;
}

const convertToNewFormat = (url, complexityData, complianceData=null, stageInventoryData=null) => {
    const { fileName, clientName, emailAddress } = parseUrl(url);

    let data = {
        assets: {},
        zipfile: fileName,
        phoneNumber: "",
        clientname: clientName,
        filePath: "",
        userEmail: emailAddress,
    };

    // NOTE: below, we check if the asset in the loop iteration is in data.assets.
    // we do this in all 3 for loops because there have been cases where asset
    // lists do not perfectly match across files due to bugs

    // Add complexity data into 'data'
    for (let assetQuery of complexityData) {
        if (!(assetQuery.asset in data.assets)) {
            data.assets[assetQuery.asset] = {
                assetType: assetQuery.assetType,
                complexityScore: {},
            };
        }
        data.assets[assetQuery.asset].complexityScore[assetQuery.query] = assetQuery.result;
    }

    if (complianceData !== null) {
        // Add compliance data into 'data'
        for (let test of complianceData) {
            if (!(test.asset in data.assets)) {
                data.assets[test.asset] = {
                    assetType: test.assetType,
                    compliance: []
                };
            } else if (!("compliance" in data.assets[test.asset])) {
                data.assets[test.asset].compliance = [];
            }

            data.assets[test.asset].compliance.push({
                result: test.result.toUpperCase(),
                duration: test.duration,
                rule: test.test,
                message: test.message
            });
        }
    } else {
        data.complianceNotAvailable = true;
    }

    if (stageInventoryData !== null) {
        // Add stage inventory data into 'data'
        for (let assetQuery of stageInventoryData) {
            if (!(assetQuery.asset in data.assets)) {
                data.assets[assetQuery.asset] = {
                    assetType: assetQuery.assetType,
                    stageTypeInventory: []
                };
            } else if (!("stageTypeInventory" in data.assets[assetQuery.asset])) {
                data.assets[assetQuery.asset].stageTypeInventory = [];
            }

            data.assets[assetQuery.asset].stageTypeInventory.push({
                stageType: assetQuery.result.split(":")[0],
                count: parseInt(assetQuery.result.split(":")[1])
            });
        }
    } else {
        data.stageInventoryNotAvailable = true;
    }

    return data;
}

const sortComplexityData = (data) => {

    data.sort((a, b) => {
        return b[OverallComplexity] - a[OverallComplexity];
    });
    return data;
}

const formatComplianceData = (compliance) => {
    let formattedCompliance = {};


    compliance.map((asset) => {
        if(!formattedCompliance[asset.asset]){
            formattedCompliance[asset.asset] = { asset : asset.asset, assetType: asset.assetType, failures: 0, warnings: 0, tests: [] };
        }
        formattedCompliance[asset.asset].tests.push({test: asset.test, result: asset.result, message: asset.message });
        if (asset.result === "Failure") formattedCompliance[asset.asset].failures++;
        if (asset.result === "Warning") formattedCompliance[asset.asset].warnings++;

        return asset;
    });

    return formattedCompliance;
}


const formatComplexityData = (data) => {
    let formatedData = {}
    data.map((asset) => {
        if(!formatedData[asset.asset]){
            formatedData[asset.asset] = { asset : asset.asset, assetType : asset.assetType }
            formatedData[asset.asset][OverallComplexity] = 0;
        }

        formatedData[asset.asset][asset.query] = !isNaN(asset.result) ? Number(asset.result) : 0;
        formatedData[asset.asset][OverallComplexity] += !isNaN(asset.result) ? Math.round(normalise(Number(asset.result), asset.query)) : 0;

        return asset;
    })

    return sortComplexityData(Object.keys(formatedData).map(asset => formatedData[asset]));
}

const formatStageInventoryData = (stageInventory) => {
    let formattedStageInventory = {};

    for (let entry of stageInventory) {
        let type = entry.result.split(":")[0];
        let count = parseInt(entry.result.split(":")[1]);

        if (!(type in formattedStageInventory)) formattedStageInventory[type] = { stageType : type, count: 0, jobs: []};

        formattedStageInventory[type].count += count;
        formattedStageInventory[type].jobs.push({"job": entry.asset, "count": count});
    }

    return formattedStageInventory; 
}

export {
    formatComplexityData,
	formatComplianceData,
    formatStageInventoryData,
    calculateTotalComplexity,
    combineCompliance,
    flattenHeirarchy,
    addNameToAssets,
    convertToNewFormat,
    normalise,
    OverallComplexity,
    parseUrl,
    getDateFromUrl
}
